<template>
    <div class="voc-feedback__container">
        <h4 class="voc-feedback__sub-workarounds">
            Workarounds
        </h4>
        <h5 class="voc-feedback__sub-title">
            In the meantime, we wanted to let you know about some workarounds attached to the related ideas. We hope
            these help!
        </h5>
        <div
            v-for="(vocItem, index) in ideasWorkarounds"
            :key="vocItem.id"
            class="ideas-workarounds"
            data-cy="voc-items-workarounds">
            <div class="ideas-workarounds__image-container">
                <img
                    class="ideas-workarounds__image"
                    src="@img/voc/workarounds.svg"
                    height="102"
                    width="132">
            </div>
            <div class="ideas-workarounds__details">
                <div
                    class="ideas-workarounds__title"
                    data-cy="voc-item-workarounds-title">
                    <div class="ideas-workarounds__header">
                        <span>Workaround last updated by</span>
                        <span v-if="vocItem.hasUsername"> {{ `${vocItem.username}` }}, </span>
                        {{ `${lastUpdatedAt(vocItem)}` }},
                        <span> associated to the Idea</span>
                        <span
                            class="ideas-workarounds__idea-link"
                            @mouseover="handleHoverOverIdeaLink(index)"
                            @mouseleave="handleHoverLeaveIdeaLink(index)">
                            <pendo-button
                                class="ideas-workarounds__idea-link-button"
                                icon-size="14"
                                type="subtle"
                                theme="app"
                                :label="truncateText(vocItem.vocItemTitle)"
                                @click="openIdeaLink(vocItem.id)" />
                            <pendo-icon
                                v-show="index === hoverOverIdeaIndex"
                                stroke="var(--palette-teal-70)"
                                class="ideas-workarounds__open-icon-small"
                                type="external-link"
                                size="14" />
                        </span>
                    </div>
                </div>
                <div>
                    <div
                        class="ideas-workarounds__wrapper"
                        :style="{ height: contentHeight + 'px' }">
                        <div class="ideas-workarounds__container">
                            <editor-content
                                class="ideas-workarounds__content"
                                :editor="getEditorForItem(vocItem)" />
                        </div>
                    </div>
                    <div>
                        <pendo-button
                            v-pendo-tooltip="{
                                arrow: true,
                                content: copyWorkaroundMessage,
                                boundariesElement: 'viewport',
                                trigger: 'manual',
                                show: index === clickedWorkaroundIndex,
                            }"
                            v-clipboard:copy="copyWorkaroundToClipboard(vocItem)"
                            v-clipboard:success="onCopySuccess"
                            v-clipboard:error="onCopyError"
                            prefix-icon="copy"
                            label="Copy"
                            theme="app"
                            type="secondary"
                            data-cy="voc-item-copy-workaround"
                            @click="handleCopyWorkaround(index)" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { PendoButton, PendoIcon, PendoTooltip } from '@pendo/components';
import { Editor, EditorContent } from '@tiptap/vue-3';
import get from 'lodash/get';
import { sanitizeContent } from '@/dashboards-v2/widgets/rich-text-widget/rich-text-widget.utils';
import isPlainObject from 'lodash/isPlainObject';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import moment, { DATE_FORMAT } from '@utils/moment-wrapper';
import {
    TEXT_ALIGNMENT_TYPES_CONFIG,
    TEXT_ALIGNMENTS_CONFIG,
} from '@/dashboards-v2/widgets/rich-text-widget/toolbar-actions/utils/text-align-options.ts';

import { mapActions, mapGetters } from 'vuex';

export default {
    name: 'VocFeedbackFormWorkarounds',
    components: {
        PendoButton,
        EditorContent,
        PendoIcon,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        ideasWorkarounds: {
            type: Array,
            default: () => [],
        },
    },
    data () {
        return {
            editor: {},
            editors: [],
            copyWorkaroundMessage: '',
            accountId: null,
            clickedWorkaroundIndex: -1,
            hoverOverIdeaIndex: -1,
        };
    },
    computed: {
        ...mapGetters({
            vocItemById: 'vocItems/vocItemById',
        }),
    },
    async created () {
        await this.loadLastUpdatedBy();
        this.initEditor();
    },
    methods: {
        ...mapActions({
            loadVocItemById: 'vocItems/loadVocItemById',
        }),
        initEditor () {
            this.ideasWorkarounds.forEach((item) => {
                const content = get(item.workaround, 'body');
                let plainContent;

                if (content) {
                    sanitizeContent(content);
                    plainContent = isPlainObject(content) ? content : '';
                }

                this.editor = new Editor({
                    content: plainContent,
                    extensions: [
                        StarterKit.configure({
                            heading: {
                                levels: [2, 3],
                            },
                        }),
                        Color,
                        TextStyle,
                        Underline,
                        Link.configure({
                            openOnClick: false,
                        }),
                        TextAlign.configure({
                            types: TEXT_ALIGNMENT_TYPES_CONFIG,
                            alignments: TEXT_ALIGNMENTS_CONFIG,
                        }),
                        Highlight.configure({
                            multicolor: true,
                        }),
                    ],
                    editorProps: {
                        attributes: {
                            spellcheck: 'false',
                        },
                    },
                    editable: false,
                });
                this.editors.push(this.editor);
            });
        },
        getEditorForItem (vocItem) {
            const index = this.ideasWorkarounds.findIndex((item) => item.id === vocItem.id);

            return index !== -1 ? this.editors[index] : null;
        },
        openIdeaLink (vocItemId) {
            return window.open(
                this.$router.pendo.toPath({
                    name: 'Validate',
                    query: { openItem: vocItemId },
                }),
            );
        },
        lastUpdatedAt (vocItem) {
            return moment(vocItem.workaround?.lastUpdatedAt).format(DATE_FORMAT.short);
        },
        async lastUpdatedBy (vocItem) {
            try {
                if (!this.vocItemById(vocItem.id)) await this.loadVocItemById({ vocItemId: vocItem.id });
                const returnedVocItem = this.vocItemById(vocItem.id);
                this.accountId = returnedVocItem.workaroundObj?.lastUpdatedByUser?.id;

                return returnedVocItem.workaroundObj?.lastUpdatedByUser?.username || this.accountId || 'Unknown user';
            } catch {
                return 'Unknown user';
            }
        },
        async loadLastUpdatedBy () {
            const lastUpdatedByUser = this.ideasWorkarounds.map(async (vocItem) => {
                const username = await this.lastUpdatedBy(vocItem);
                vocItem.username = username;
                vocItem.hasUsername = username !== 'Unknown user';
            });
            await Promise.all(lastUpdatedByUser);
        },
        copyWorkaroundToClipboard (vocItem) {
            return this.getEditorForItem(vocItem)?.getText() || '';
        },
        resetCopyWorkaroundMessage () {
            setTimeout(() => {
                this.copyWorkaroundMessage = '';
            }, 1000);
        },
        onCopySuccess () {
            this.copyWorkaroundMessage = 'Workaround copied!';
        },
        onCopyError () {
            this.copyWorkaroundMessage = 'Workaround copy failed!';
        },
        handleCopyWorkaround (index) {
            this.clickedWorkaroundIndex = index;
            this.resetCopyWorkaroundMessage();
        },
        handleHoverOverIdeaLink (index) {
            this.hoverOverIdeaIndex = index;
        },
        handleHoverLeaveIdeaLink (index) {
            if (this.hoverOverIdeaIndex === index) {
                this.hoverOverIdeaIndex = -1;
            }
        },
        truncateText (vocItemTitle) {
            if (vocItemTitle && vocItemTitle.length < 30) {
                return vocItemTitle;
            }
            const shortenedText = vocItemTitle.substr(0, 31);

            return `${shortenedText}...`;
        },
        contentHeight () {
            const container = this.$el;

            return container.scrollHeight;
        },
    },
};
</script>

<!-- eslint-disable vue-scoped-css/no-parsing-error -->
<style scoped lang="less">
.voc-feedback {
    &__container {
        padding: 0 16px 0 16px;
    }
    &__sub-title {
        font-weight: 400;
        position: relative;
        margin-bottom: 24px;
    }

    &__sub-workarounds {
        font-weight: 600;
        position: relative;
        margin-bottom: 16px;
    }
}
.ideas-workarounds {
    background-color: var(--palette-gray-0);
    border: 1px solid var(--palette-gray-40);
    border-radius: 5px;
    padding: 16px;
    margin: 16px 0;
    display: flex;
    gap: 10px;
    &__image-container {
        justify-content: center;
    }
    &__image {
        margin: 16px;
        height: 102px;
        width: 132;
    }
    &__details {
        overflow-wrap: break-word;
        width: 468px;
        overflow: auto;
        align-self: flex-start;
    }
    &__title {
        font-weight: 400;
        font-size: 14px;
        margin-bottom: 5px;
        color: var(--palette-gray-70);
    }
    &__header {
        line-height: 20px;
    }
    &__idea-link {
        position: relative;
    }
    &__idea-link-button {
        height: 0px;
        margin-right: 6px;
    }
    &__open-icon-small {
        top: 0;
        right: -15px;
        position: absolute;
        pointer-events: none;
    }
    &__wrapper {
        justify-content: center;
        position: relative;
        height: 100%;
        user-select: text;
        padding: 16px 0;
    }
    &__container {
        display: flex;
        flex-grow: 1;
        position: relative;
    }
    &__content {
        width: 100%;
        padding-bottom: 16px;
    }
    .tippy-content {
        padding: 0;
    }
    .tippy-arrow {
        visibility: hidden;
    }
    :deep(.ProseMirror) {
        height: 100%;
        width: 100%;
        overflow: auto;
        overflow-wrap: anywhere;
        outline: none;
        line-height: 150%;
        p {
            font-size: 14px;
            &.is-editor-empty:first-child::before {
                content: attr(data-placeholder);
                float: left;
                color: var(--palette-gray-70);
                font-style: italic;
                pointer-events: none;
                height: 0;
            }
            &:last-child {
                margin: 0;
            }
            &:last-child {
                margin: 0;
            }
        }
        h2 {
            font-size: 32px;
            font-weight: 400;
        }
        h3 {
            font-size: 24px;
            font-weight: 400;
        }
        // Links should always be colored like links: users are not allowed to customize color.
        // Any other styles (size, weight, italics, etc) are allowed to be configured.
        a * {
            color: var(--palette-teal-70) !important;
        }
        p > code {
            background-color: var(--palette-gray-40);
            color: var(--palette-gray-100);
        }
        hr {
            border-top: 1px solid var(--palette-gray-70);
        }
        ol,
        ul {
            & > li {
                list-style-position: inside;
                & > p {
                    display: inline-block;
                    max-width: calc(100% - 20px);
                    vertical-align: top;
                }
            }
        }
    }
    // Since Safari does not support the "anywhere" value on overflow-wrap, we fallback to this
    // deprecated property value which has the same effect.
    @supports not (overflow-wrap: anywhere) {
        :deep(.ProseMirror) {
            word-break: break-word;
        }
    }
}
</style>
